import type { components } from '@/api/schema';

import { labelOf } from './humanize';

type Schemas = components['schemas'];

export type AiScope = Schemas['AiActionRequest']['scopeType'];

/** Why the assistant's answer was held back; the schema carries these as plain strings. */
export type AssistantBlockCode =
  | 'CHAT_TECHNICAL_DIAGNOSIS'
  | 'CHAT_FINAL_EQUIPMENT_SELECTION'
  | 'CHAT_UNAPPROVED_PROMISE'
  | 'CHAT_PRESSURE_DECEPTION'
  | 'CHAT_FALSE_ACTION_COMPLETION'
  | 'CHAT_SAFETY_DETERMINATION'
  | 'CHAT_COMMERCIAL_TERMS'
  | 'CHAT_UNGROUNDED_WORKSPACE_CLAIM'
  | 'CHAT_CONFIDENTIAL_DISCLOSURE'
  | 'CHAT_PERFORMANCE_JUDGEMENT'
  | 'CHAT_RECORDING_CONSENT'
  | 'CHAT_REVIEW_UNCERTAIN'
  | 'CHAT_REVIEW_UNAVAILABLE'
  | 'CHAT_REVIEW_INVALID'
  | 'INVALID_STRUCTURE'
  | 'CITATION_OR_UNKNOWNS'
  | 'UNAPPROVED_CONTENT'
  | 'COMMERCIAL_TERMS'
  | 'CONFIDENTIAL_DISCLOSURE'
  | 'UNAPPROVED_CUSTOMER_CONTACT'
  | 'FINAL_EQUIPMENT_SELECTION'
  | 'UNSOURCED_PRICE'
  | 'PRICEBOOK_AMOUNT_MISMATCH'
  | 'PERFORMANCE_JUDGEMENT'
  | 'PRESSURE_OR_DECEPTION'
  | 'RECORDING_WITHOUT_CONSENT'
  | 'SAFETY_DETERMINATION'
  | 'TECHNICAL_DIAGNOSIS'
  | 'UNAPPROVED_PROMISE'
  | 'BINDING_ACCEPTANCE'
  | 'SCOPE_OR_PERMISSION_DENIED'
  | 'REQUEST_FAILED';

/**
 * The kinds of record the server cites as a source; the schema carries the kind as a plain string.
 * "Service history" and "Pricebook language" are sent as wording already and need no entry.
 */
export type AiSourceType =
  | 'Customer'
  | 'Opportunity'
  | 'Equipment'
  | 'Appointment'
  | 'Pricebook'
  | 'Consultation'
  | 'Property'
  | 'FollowUpTask'
  | 'JobPackage'
  | 'JobPackageViolation'
  | 'ReplacementHandoff'
  | 'EquipmentServiceRecord'
  | 'ComparisonWorksheet';

// APPOINTMENT is how the server names a job record in the assistant's scope list, and OPPORTUNITY
// a deal; the product says job and deal everywhere else.
export const AI_SCOPE_LABELS: Record<AiScope, string> = {
  CUSTOMER: 'Customer',
  OPPORTUNITY: 'Deal',
  EQUIPMENT: 'Equipment history',
  PRICING_REFERENCE: 'Pricing reference',
  APPOINTMENT: 'Job',
};

// Some of these arrive as class names. A source chip names the record in the words the rest of the
// product uses: an appointment is a job, and a package violation is one of its open checks.
export const AI_SOURCE_LABELS: Record<AiSourceType, string> = {
  Customer: 'Customer',
  Opportunity: 'Opportunity',
  Equipment: 'Equipment',
  Appointment: 'Job',
  Pricebook: 'Pricebook',
  Consultation: 'Consultation',
  Property: 'Property',
  FollowUpTask: 'Follow-up',
  JobPackage: 'Job package',
  JobPackageViolation: 'Package check',
  ReplacementHandoff: 'Replacement handoff',
  EquipmentServiceRecord: 'Service record',
  ComparisonWorksheet: 'Comparison worksheet',
};

// The chat guardrails and the record-assistant validators refuse the same things; one sentence
// each, so a person reads the same explanation wherever the assistant is held back.
export const ASSISTANT_BLOCK_LABELS: Record<AssistantBlockCode, string> = {
  CHAT_TECHNICAL_DIAGNOSIS: 'A qualified technician must diagnose equipment.',
  CHAT_FINAL_EQUIPMENT_SELECTION:
    'An authorized professional must choose equipment and system size.',
  CHAT_UNAPPROVED_PROMISE: 'Savings, availability and timing claims need approved evidence.',
  CHAT_PRESSURE_DECEPTION: 'The generated answer included pressure or deceptive wording.',
  CHAT_FALSE_ACTION_COMPLETION:
    'General chat cannot contact customers or change records. Use the relevant workflow for reviewed actions.',
  CHAT_SAFETY_DETERMINATION: 'A qualified professional must assess equipment and site safety.',
  CHAT_COMMERCIAL_TERMS: 'Commercial terms require the authorized pricing and approval workflow.',
  CHAT_UNGROUNDED_WORKSPACE_CLAIM:
    'The answer could not be supported by the available workspace data.',
  CHAT_CONFIDENTIAL_DISCLOSURE:
    'The answer included information outside your permitted workspace context.',
  CHAT_PERFORMANCE_JUDGEMENT: 'Personnel and compensation decisions require human review.',
  CHAT_RECORDING_CONSENT: 'Recording requires the appropriate consent.',
  CHAT_REVIEW_UNCERTAIN:
    'The answer could not be verified. Please clarify the request or use the relevant record.',
  CHAT_REVIEW_UNAVAILABLE: 'The answer could not be verified right now. Please try again.',
  CHAT_REVIEW_INVALID: 'The answer could not be verified. Please try again.',
  INVALID_STRUCTURE: 'The answer was not in the required format.',
  CITATION_OR_UNKNOWNS: 'The answer was missing its sources or a list of what is unknown.',
  UNAPPROVED_CONTENT: 'The answer included content that has not been approved.',
  COMMERCIAL_TERMS: 'Commercial terms require the authorized pricing and approval workflow.',
  CONFIDENTIAL_DISCLOSURE:
    'The answer included information outside your permitted workspace context.',
  UNAPPROVED_CUSTOMER_CONTACT: 'Contacting a customer needs a person to approve it first.',
  FINAL_EQUIPMENT_SELECTION: 'An authorized professional must choose equipment and system size.',
  UNSOURCED_PRICE: 'A price in the answer did not come from the pricebook.',
  PRICEBOOK_AMOUNT_MISMATCH: 'An amount in the answer does not match the pricebook.',
  PERFORMANCE_JUDGEMENT: 'Personnel and compensation decisions require human review.',
  PRESSURE_OR_DECEPTION: 'The generated answer included pressure or deceptive wording.',
  RECORDING_WITHOUT_CONSENT: 'Recording requires the appropriate consent.',
  SAFETY_DETERMINATION: 'A qualified professional must assess equipment and site safety.',
  TECHNICAL_DIAGNOSIS: 'A qualified technician must diagnose equipment.',
  UNAPPROVED_PROMISE: 'Savings, availability and timing claims need approved evidence.',
  BINDING_ACCEPTANCE: 'The assistant cannot accept anything on a customer’s behalf.',
  SCOPE_OR_PERMISSION_DENIED: 'You do not have access to that record.',
  REQUEST_FAILED: 'The request could not be completed.',
};

export function aiScopeLabel(scope: string | null | undefined): string {
  return labelOf(AI_SCOPE_LABELS, scope);
}

export function aiSourceLabel(type: string | null | undefined): string {
  return labelOf(AI_SOURCE_LABELS, type);
}

export function assistantBlockLabel(code: string | null | undefined): string {
  return labelOf(ASSISTANT_BLOCK_LABELS, code);
}
